import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { CodeSnippet, getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { StepContainer } from '../step-container/step-container';
import { GoalCreationWizardContext } from '../../goal-creation-wizard-context';
import { InsertInstruction } from '../components/insert-instructions';
import { WIZARD_ACTIONS } from '../../goal-creation-wizard-reducer';
import { getTagTemplates } from '../utils/tag-template';
import { CopyDownloadEnhancedConvTag } from '../components/copy-download-enhanced';

const SetUpEnhancedConversionsUnstyled = (props) => {
  const {
    classes,
    permissions,
  } = props;

  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const { logActivity } = wizardContext;
  const goalObj = useMemo(() => wizardContext.goalContext, [wizardContext.goalContext]);
  const template = getTagTemplates(i18n);

  const handleCancel = useCallback(() => {
    wizardContext.exitWizard();
  }, [wizardContext]);

  const handleNext = useCallback(() => {
    const { Type, GoalValueType } = goalObj;

    const goToEventTagSetup = Type === 'EventGoal' || Type === 'ProductConversionGoal'
    || GoalValueType === 'VariantValue';

    wizardContext.dispatch({
      type: goToEventTagSetup ?
        WIZARD_ACTIONS.NAVIGATE_WITH_TAG_INSTALL_INSTRUCTIONS :
        WIZARD_ACTIONS.WIZARD_NAVIGATE_NEXT,
      data: {},
    });
  }, [goalObj, wizardContext]);

  const footerConfig = useMemo(() => ({
    buttons: [{
      type: 'Secondary',
      onClick: wizardContext.goBack,
      isEnabled: true,
      text: i18n.getString(_TL_(('Back'))),
    },
    {
      type: 'Primary',
      onClick: handleNext,
      isEnabled: true,
      text: i18n.getString(_TL_(('Next'))),
    },
    {
      type: 'Cancel',
      onClick: handleCancel,
      isEnabled: true,
      text: i18n.getString(_TL_(('Cancel'))),
    }],
  }), [wizardContext.goBack, i18n, handleNext, handleCancel]);

  const copyCallback = useCallback((res) => {
    if (res && res.error) {
      logActivity.error(JSON.stringify(res));
    }
    logActivity.trace('enhanced conversion tag copy clicked');
  }, [logActivity]);

  const downloadCallback = useCallback((res) => {
    if (res && res.error) {
      logActivity.error(JSON.stringify(res));
    }
    logActivity.trace('enhanced conversion tag download clicked');
  }, [logActivity]);

  return (
    <StepContainer
      subContainer
      step="set-up-enhanced-conversions"
      footerConfig={footerConfig}
      title={i18n.getString(_TL_('Set up enhanced conversions'))}
    >
      <div>
        <div className={classNames(classes.contentBlock)}>
          <div
            className={classes.section}
            style={{
            marginBottom: '20px',
          }}
          >
            <InsertInstruction
              i18n={i18n}
              template={template.EnhancedConvInstructions}
              logActivity={logActivity}
              wizardDispatch={wizardContext.dispatch}
              permissions={permissions}
              hashingInstructions={template.HashingInstructions}
            />
          </div>
          <div
            className={classes.section}
            style={{
            marginBottom: '20px',
          }}
          >
            <CodeSnippet
              codeTemplate={template.EnhancedConvCode.join('\n')}
            />
          </div>
          <div
            className={classes.section}
            style={{
            marginBottom: '20px',
          }}
          >
            <CopyDownloadEnhancedConvTag
              instructionsTemplate={template.EnhancedConvInstructions.slice(0, -1)}
              codeTemplate={template.EnhancedConvCode.join('\n')}
              onCopyCallback={copyCallback}
              onDownloadCallback={downloadCallback}
              tagTemplates={wizardContext.tagTemplates}
              logActivity={logActivity}
              wizardDispatch={wizardContext.dispatch}
              permissions={permissions}
            />
          </div>
        </div>
      </div>
    </StepContainer>

  );
};

SetUpEnhancedConversionsUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  permissions: PropTypes.shape({}).isRequired,
};

export const SetUpEnhancedConversions = withPerfMarker(withDefaultStyles(SetUpEnhancedConversionsUnstyled, getStepStyles));
